<template>
  <v-card flat color="transparent" class="note-item--card d-flex flex-column">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="checkbox ml-3 mt-3"
      :checked="isSelected"
      @click="toggleSelect"
    />

    <!-- Colored preview area mimicking bookmark thumbnail -->
    <div
      class="note-preview-container rounded"
      :style="previewStyle"
      @click="onPreviewClick"
    >
      <div
        v-if="sanitizedContent"
        class="note-preview-content text-neutral900"
        v-html="sanitizedContent"
      />
    </div>

    <div
      class="px-0 pt-3 mb-1 note-headline text-body-1 font-weight-semibold text-neutral900"
      @click="onTitleClick"
    >
      {{ note.title || $t('common.untitled_note') }}
    </div>

    <div class="text-neutral600 d-flex align-center mb-3 text-caption">
      <NoteSource :note="note" />
      <span class="mx-1">&middot;</span>
      <NoteDate :value="note.createdAt" />
    </div>

    <NoteTags v-if="note.tags?.length" class="mb-3" :tags="note.tags" @tag-selected="onTagSelected" />

    <v-spacer />

    <NoteActions
      :key="note.id"
      :note="note"
      :user-id="userId"
      :public-mode="publicMode"
      :membership="membership"
      class="text-text"
      @favorite="$emit('favorite')"
      @save="$emit('save')"
      @add-to-collection="$emit('add-to-collection')"
      @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
      @comments="$emit('comments')"
      @like="$emit('like')"
      @likes="$emit('likes')"
      @pin-edit="$emit('pin-edit')"
    />
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import NoteSource from './NoteSource.vue'
import NoteActions from './NoteActions.vue'
import NoteDate from './NoteDate.vue'
import NoteTags from './NoteTags.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'
import { useNoteColors } from '@/utils/noteColors'
import { convertHeadingsToBold } from '@/utils/sanitize'

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const router = useRouter()
const route = useRoute()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()
const { getNoteColor } = useNoteColors()

const isSelected = computed(() => {
  return isItemSelected(props.note.id)
})

const previewStyle = computed(() => {
  return {
    backgroundColor: getNoteColor(props.note.color)
  }
})

const sanitizedContent = computed(() => {
  return convertHeadingsToBold(props.note.contentPreview || '')
})

function onPreviewClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    navigateToDetail()
  }
}

function onTitleClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    navigateToDetail()
  }
}

function navigateToDetail() {
  if (props.publicMode) {
    const { username, id } = route.params
    router.push(`/u/${username}/collections/${id}/notes/${props.note.id}`)
    return
  }
  if (props.note.isPrivate) {
    router.push(`/notes/${props.note.id}`)
  } else {
    router.push(`/collections/${props.note.ownerId}/notes/${props.note.id}`)
  }
}

function toggleSelect() {
  emit('toggle-select', props.note)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.note-item--card {
  height: 100%;

  .checkbox {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }
}

.note-preview-container {
  position: relative;
  aspect-ratio: 16 / 9;
  padding: 20px 24px 0;
  cursor: pointer;
  overflow: hidden;
}

.note-preview-content {
  overflow: hidden;
  word-break: break-word;
  font-size: 0.95rem;
  line-height: 1.4;
  margin: 0;
  height: 100%;
  user-select: none;

  // Rich text content styles - scaled down for preview
  :deep(p) {
    margin: 0 0 0.4em;
    font-size: inherit;
    line-height: inherit;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(strong),
  :deep(b) {
    font-weight: 600;
  }

  :deep(em),
  :deep(i) {
    font-style: italic;
  }

  :deep(u) {
    text-decoration: underline;
  }

  :deep(a) {
    color: rgb(var(--v-theme-blue600));
    text-decoration: underline;
    pointer-events: none; // Disable link clicks in preview
  }

  // Lists - compact for preview
  :deep(ul),
  :deep(ol) {
    padding-left: 1.2em;
    margin: 0.3em 0;
    font-size: inherit;
    line-height: inherit;
  }

  :deep(ul) {
    list-style-type: disc;
  }

  :deep(ol) {
    list-style-type: decimal;
  }

  :deep(li) {
    margin: 0.15em 0;

    p {
      margin: 0;
    }
  }

  // Blockquote - compact
  :deep(blockquote) {
    border-left: 2px solid rgba(0, 0, 0, 0.2);
    padding-left: 0.6em;
    margin: 0.3em 0;
    font-style: italic;
    opacity: 0.9;
  }

  // Code - scaled down
  :deep(pre) {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    padding: 6px 8px;
    margin: 0.3em 0;
    overflow: hidden;
    font-size: 0.7rem;

    code {
      background: none;
      padding: 0;
      font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
    }
  }

  :deep(code) {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 0.7rem;
    font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  }

  // Tables - compact for preview
  :deep(table) {
    border-collapse: collapse;
    margin: 0.3em 0;
    font-size: 0.65rem;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    overflow: hidden;

    th,
    td {
      border: 1px solid rgba(0, 0, 0, 0.15);
      padding: 3px 5px;
      text-align: left;

      p {
        margin: 0;
      }
    }

    th {
      background-color: rgba(0, 0, 0, 0.04);
      font-weight: 600;
    }
  }

  // Headers - scaled down
  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-size: 0.8rem;
    font-weight: 600;
    margin: 0.3em 0;
    line-height: 1.3;
  }
}

.note-headline {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.4 !important;
  cursor: pointer;
}
</style>
